/**
 * Connection validation for canvas nodes.
 *
 * Package YAML ports often use `$ref: "canvas-artifacts/..."`. Those refs are
 * agent/provisioner contracts — the FE must not ask Ajv to resolve them.
 * Deploy readiness is: required *input* handles have edges. Output `$ref`s are
 * produced by the provisioner, not required as connections before deploy.
 */

/** Handles that must be wired per JSON Schema `required` on inputs. */
export const getMissingRequiredInputHandles = (inputs, connectedHandles) => {
  if (!inputs?.properties) return [];
  const required = inputs.required || [];
  return required.filter((req) => !connectedHandles.includes(req));
};

/** @deprecated Output edges are not a deploy precondition; kept for callers. */
export const getMissingRequiredOutputHandles = () => [];

/**
 * Look up a handle's property schema from an inputs/outputs object.
 * Null-safe when `properties` is missing (fail closed).
 */
export const getHandlePropertySchema = (ports, handleId) => {
  if (!ports?.properties || handleId == null || handleId === '') return null;
  const schema = ports.properties[handleId];
  return schema && typeof schema === 'object' ? schema : null;
};

/**
 * Whether a source output schema may connect to a target input schema.
 * Prefer identical `$ref` (e.g. canvas-artifacts/gcp/vpc); otherwise require
 * equal `type`. Fail closed when either side lacks a comparable identity.
 */
const isLegacyObjectPort = (schema) =>
  Boolean(schema && schema.type === 'object' && !schema.$ref);

const artifactRefTail = (ref) => {
  if (typeof ref !== 'string' || !ref) return '';
  const parts = ref.split('/');
  return parts[parts.length - 1] || '';
};

/**
 * Older catalog nodes stored subnet/network as `{type: object, properties:{id}}`
 * while current packages emit `$ref: canvas-artifacts/gcp/subnet` (etc).
 * Match when the artifact name is the handle (vpc also aliases `network`).
 */
export const legacyArtifactPortCompatible = (
  sourceSchema,
  targetSchema,
  sourceHandle,
  targetHandle
) => {
  const srcRef = sourceSchema?.$ref;
  const tgtRef = targetSchema?.$ref;
  const handleMatches = (ref, handle) => {
    const tail = artifactRefTail(ref);
    if (!tail || !handle) return false;
    if (tail === handle) return true;
    return tail === 'vpc' && handle === 'network';
  };
  if (srcRef && !tgtRef && isLegacyObjectPort(targetSchema)) {
    return handleMatches(srcRef, targetHandle);
  }
  if (tgtRef && !srcRef && isLegacyObjectPort(sourceSchema)) {
    return handleMatches(tgtRef, sourceHandle);
  }
  return false;
};

export const arePortSchemasCompatible = (sourceSchema, targetSchema) => {
  if (!sourceSchema || !targetSchema) return false;

  const sourceRef = sourceSchema.$ref;
  const targetRef = targetSchema.$ref;
  if (sourceRef || targetRef) {
    return Boolean(sourceRef && targetRef && sourceRef === targetRef);
  }

  const sourceType = sourceSchema.type;
  const targetType = targetSchema.type;
  if (sourceType == null || targetType == null) return false;
  return sourceType === targetType;
};

/**
 * Direction + schema check for a prospective edge.
 * Only output → input is allowed; schemas must be compatible.
 *
 * @returns {{ valid: boolean, reason?: string }}
 */
export const evaluateConnection = ({
  sourceNode,
  targetNode,
  sourceHandle,
  targetHandle,
}) => {
  if (!sourceNode?.data || !targetNode?.data) {
    return { valid: false, reason: 'Missing node data for connection' };
  }
  if (sourceNode.id === targetNode.id) {
    return { valid: false, reason: 'Cannot connect a node to itself' };
  }

  const sourceSchema = getHandlePropertySchema(
    sourceNode.data.outputs,
    sourceHandle
  );
  const targetSchema = getHandlePropertySchema(
    targetNode.data.inputs,
    targetHandle
  );

  // Direction: sourceHandle must be an output; targetHandle must be an input.
  // out→out / in→in / in→out fail because the handle is absent from that side.
  if (!sourceSchema) {
    return {
      valid: false,
      reason: 'Connections must start from an output handle',
    };
  }
  if (!targetSchema) {
    return {
      valid: false,
      reason: 'Connections must end on an input handle',
    };
  }

  if (
    !arePortSchemasCompatible(sourceSchema, targetSchema) &&
    !legacyArtifactPortCompatible(
      sourceSchema,
      targetSchema,
      sourceHandle,
      targetHandle
    )
  ) {
    return {
      valid: false,
      reason: 'Incompatible port schemas for this connection',
    };
  }

  return { valid: true };
};

export const isValidEdgeConnection = (args) => evaluateConnection(args).valid;

export const validateConnections = (nodeData, edges = []) => {
  const inputs = nodeData?.inputs || {};
  const connectedInputs = edges
    .filter((edge) => edge.target === nodeData.id)
    .map((edge) => edge.targetHandle);

  const missingInputs = getMissingRequiredInputHandles(inputs, connectedInputs);

  return {
    valid: missingInputs.length === 0,
    missingInputs,
    missingOutputs: [],
  };
};
